import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { Toaster } from "./components/ui/sonner";

// Components
import Navigation from "./components/Navigation";
import Footer from "./components/Footer";
import PageViewTracker from "./components/PageViewTracker";
import LogoWatermark from "./components/LogoWatermark";
import ChatWidget from "./components/ChatWidget";
import { SpotifyMiniPlayer } from "./components/SpotifyPlayer";

// Pages
import HomePage from "./pages/HomePage";
import CatalogPage from "./pages/CatalogPage";
import PacksPage from "./pages/PacksPage";
import MembershipPage from "./pages/MembershipPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import AdminPage from "./pages/AdminPage";
import CheckoutPage, { CheckoutCancelPage } from "./pages/CheckoutPage";
import ArtistsPage from "./pages/ArtistsPage";
import AboutPage from "./pages/AboutPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import RefundPage from "./pages/RefundPage";
import LicensingPage from "./pages/LicensingPage";
import FAQPage from "./pages/FAQPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import SetPasswordPage from "./pages/SetPasswordPage";
import AssistantPage from "./pages/AssistantPage";
import PayPalSubscriptionSuccessPage from "./pages/PayPalSubscriptionSuccessPage";
import UltimatePackPage from "./pages/UltimatePackPage";
import ServicesPage from "./pages/ServicesPage";
import PlaylistPromoPage from "./pages/PlaylistPromoPage";

import AuthCallback from "./components/AuthCallback";

function AppLayout() {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id from Google OAuth BEFORE any route
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  const isPanel = location.pathname.startsWith('/admin') || location.pathname.startsWith('/assistant');
  const isLogin = location.pathname === '/login';
  return (
    <div className="App min-h-screen bg-[#050507]">
      <LogoWatermark />
      <PageViewTracker />
      {!isPanel && <Navigation />}
      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/catalog" element={<CatalogPage />} />
          {/* Mastering eliminado del frontend público (Feb 2026 pivot). Redirigimos a Packs. */}
          <Route path="/mastering" element={<Navigate to="/packs" replace />} />
          <Route path="/mastering-og" element={<Navigate to="/packs" replace />} />
          <Route path="/paypal-subscription-success" element={<PayPalSubscriptionSuccessPage />} />
          <Route path="/ultimate" element={<UltimatePackPage />} />
          <Route path="/packs" element={<PacksPage />} />
          <Route path="/membership" element={<MembershipPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/assistant" element={<AssistantPage />} />
          <Route path="/artists" element={<ArtistsPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/refund" element={<RefundPage />} />
          <Route path="/licensing" element={<LicensingPage />} />
          <Route path="/licenses" element={<Navigate to="/membership" replace />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/playlist-promotion" element={<PlaylistPromoPage />} />
          <Route path="/playlist-push" element={<Navigate to="/playlist-promotion" replace />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/set-password" element={<SetPasswordPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/checkout/success" element={<CheckoutPage />} />
          <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
        </Routes>
      </main>
      {!isPanel && <Footer />}
      {!isPanel && <ChatWidget />}
      {!isPanel && !isLogin && <SpotifyMiniPlayer />}
      <Toaster position="bottom-right" />
    </div>
  );
}

function App() {
  return (
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppLayout />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
